import type { ApolloCache } from '@apollo/client';
import { useMutation } from '@apollo/client';

import {
  FollowCompanyDocument,
  UnfollowCompanyDocument,
} from '@xing-com/crate-jobs-graphql';

type UseToggleFollowCompanyResult = {
  readonly onToggleFollow: () => void;
};

const updateIsFollowing = (
  cache: ApolloCache<unknown>,
  companyId: string,
  isFollowing: boolean
): void => {
  cache.modify({
    id: cache.identify({ __typename: 'Company', id: companyId }),
    fields: {
      userContext(cachedUserContext) {
        return {
          ...cachedUserContext,
          followState: {
            isFollowing,
          },
        };
      },
    },
  });
};

type UseToggleFollowProp = {
  companyId: string;
  currentIsFollowing: boolean;
  onCompleted?: () => void;
  onError?: () => void;
};

export const useToggleFollowCompany = ({
  companyId,
  currentIsFollowing,
  onCompleted,
  onError,
}: UseToggleFollowProp): UseToggleFollowCompanyResult => {
  const [onFollowCompany] = useMutation(FollowCompanyDocument, {
    variables: { input: { followedId: companyId } },
    optimisticResponse: {
      __typename: 'Mutation',
      companyFollow: { error: null, __typename: 'FollowersMutationResult' },
    },
    update: (cache) => updateIsFollowing(cache, companyId, true),
    onError,
    onCompleted,
  });

  const [onUnfollowCompany] = useMutation(UnfollowCompanyDocument, {
    variables: { input: { followedId: companyId } },
    optimisticResponse: {
      __typename: 'Mutation',
      companyUnfollow: { error: null, __typename: 'FollowersMutationResult' },
    },
    update: (cache) => updateIsFollowing(cache, companyId, false),
    onError,
    onCompleted,
  });

  const onToggleFollow = currentIsFollowing
    ? onUnfollowCompany
    : onFollowCompany;

  return { onToggleFollow };
};
